import io from 'socket.io-client';
import { toast } from 'react-toastify';

// Import Services
import ScriptService from './scriptService';
import ShotService from './shotService';
import Utils from './utilities';

let exportUpdateCallback = null;
export function registerExportUpdateCallback(cb) {
  exportUpdateCallback = cb;
}

let socket;

const updateScene = (setScript, processScene) => {
  setScript((existingScript) => {
    if (existingScript && existingScript.scenes && processScene) {
      const updatedScenes = existingScript.scenes.map((scene) => {
        if (scene.id === processScene.id) {
          return {
            ...scene,
            ai_status: processScene.ai_status,
            isGeneratingShots: processScene.isGeneratingShots,
            generation_error: processScene.generation_error,
            shot_list: processScene.shot_list,
            portions: processScene.portions,
          };
        }
        return scene;
      });
      return {
        ...existingScript,
        scenes: updatedScenes,
      };
    } else {
      console.error('Script or script.scenes is undefined.');
      return existingScript;
    }
  });
};

const addScene = (setScript, newScene) => {
  setScript((prevScript) => {
    const foundScene = prevScript.scenes.find((scene) => scene.id === newScene.id);
    if (foundScene) {
      return prevScript;
    }

    newScene.shot_list = Utils.buildTempShot(prevScript, newScene);
    const updatedScenes = [...prevScript.scenes, newScene].sort(
      (a, b) => a.scene_position - b.scene_position
    );

    return { ...prevScript, scenes: updatedScenes };
  });
};

const updateSceneManual = (setScript, sceneId, field, newValue) => {
  setScript((prevScript) => {
    const updatedScenes = prevScript.scenes.map((scene) => {
      if (scene.id === sceneId) {
        return {
          ...scene,
          [field]: newValue,
        };
      }
      return scene;
    });
    return { ...prevScript, scenes: updatedScenes };
  });
};

const findSceneIndexByShotId = (scenes, shotId) => {
  for (let i = 0; i < scenes.length; i++) {
    const scene = scenes[i];

    if (!scene || !Array.isArray(scene.shot_list)) {
      continue;
    }
    const foundShot = scene.shot_list.find((shot) => shot.id == shotId);
    if (foundShot) {
      return i;
    }
  }
  return -1;
};
const addShot = (script, setScript, sceneId, newShot) => {
  const foundScene = script.scenes.find((scene) => scene.id === sceneId);
  if (!foundScene) return;
  const updatedShotList = [...foundScene.shot_list, newShot].sort(
    (a, b) => a.shot_number - b.shot_number
  );
  const updatedScenes = script.scenes.map((scene) =>
    scene.id === sceneId ? { ...scene, shot_list: updatedShotList } : scene
  );
  setScript({ ...script, scenes: updatedScenes });
};

const updateShot = async (script, setScript, shotId, field, newValue) => {
  try {
    // Resolve async values before touching state to avoid stale-snapshot race conditions
    let resolvedValue = newValue;
    if (field === 'image') {
      resolvedValue =
        !newValue || newValue.length < 1 ? null : await ShotService.retrieveImg(shotId);
    } else if (field === 'FX' || field === 'VFX') {
      resolvedValue = newValue ? 1 : 0;
    }

    setScript((prevScript) => {
      const sceneIndex = findSceneIndexByShotId(prevScript.scenes, shotId);
      if (sceneIndex === -1) return prevScript;

      const prevScene = prevScript.scenes[sceneIndex];
      if (!prevScene || !Array.isArray(prevScene.shot_list)) return prevScript;

      const shotIndex = prevScene.shot_list.findIndex((s) => s.id == shotId);
      if (shotIndex === -1) return prevScript;

      const updatedShotList = [...prevScene.shot_list];

      if (field === 'full') {
        updatedShotList[shotIndex] = resolvedValue;
      } else if (field === 'shot_number') {
        updatedShotList[shotIndex] = { ...updatedShotList[shotIndex], shot_number: resolvedValue };
        updatedShotList.sort((a, b) => a.shot_number - b.shot_number);
      } else if (field === 'deleted_at') {
        updatedShotList.splice(shotIndex, 1);
      } else if (field === 'imageIsLoading') {
        updatedShotList[shotIndex] = {
          ...updatedShotList[shotIndex],
          imageIsLoading: !!resolvedValue,
          // Starting a new generation attempt clears any error left over from a previous failure.
          imageGenerationFailed: resolvedValue ? false : updatedShotList[shotIndex].imageGenerationFailed,
          imageGenerationBlocked: resolvedValue ? false : updatedShotList[shotIndex].imageGenerationBlocked,
        };
      } else if (field === 'image') {
        updatedShotList[shotIndex] = {
          ...updatedShotList[shotIndex],
          image: resolvedValue,
          imageIsLoading: false,
          imageGenerationFailed: false,
          imageGenerationBlocked: false,
        };
      } else if (field === 'imageGenerationFailed') {
        updatedShotList[shotIndex] = {
          ...updatedShotList[shotIndex],
          imageIsLoading: false,
          // A content-policy rejection is terminal and reported separately,
          // so the generic failure the job reconcile sends afterwards must
          // not downgrade it into something we offer a retry for.
          imageGenerationFailed: updatedShotList[shotIndex].imageGenerationBlocked
            ? false
            : !!resolvedValue,
        };
      } else if (field === 'imageGenerationBlocked') {
        // Permanent failure (e.g. the shot description was rejected by the
        // image model's content policy) — don't offer a retry, since the
        // same description will just be rejected again.
        updatedShotList[shotIndex] = {
          ...updatedShotList[shotIndex],
          imageIsLoading: false,
          imageGenerationFailed: false,
          imageGenerationBlocked: !!resolvedValue,
        };
      } else {
        updatedShotList[shotIndex] = { ...updatedShotList[shotIndex], [field]: resolvedValue };
      }

      const updatedScenes = [...prevScript.scenes];
      updatedScenes[sceneIndex] = { ...prevScene, shot_list: updatedShotList };
      return { ...prevScript, scenes: updatedScenes };
    });

    return true;
  } catch (error) {
    console.error('Error updating shot:', error);
    return false;
  }
};

const sortByRequestedOnDesc = (a, b) =>
  new Date(b.requested_on || 0) - new Date(a.requested_on || 0);

const updateExportProgress = async (jobId, jobDetail, setProjectExports) => {
  if (!jobId) return;
  setProjectExports((prev) => {
    const idx = prev.findIndex((e) => e.id === jobId);

    if (idx === -1) {
      return [jobDetail, ...prev].sort(sortByRequestedOnDesc);
    }

    const current = prev[idx];
    const updated = {
      ...current,
      status: jobDetail.status ?? current.status,
      progress_percent:
        typeof jobDetail.progress_percent === 'number'
          ? jobDetail.progress_percent
          : current.progress_percent ?? 0,
      stage: jobDetail.stage ?? current.stage,
      export_path: jobDetail.export_path ?? current.export_path,
      shot_count: jobDetail.shot_count ?? current.shot_count,
      includes_images: jobDetail.includes_images ?? current.includes_images,
      stalled_seconds:
        typeof jobDetail.stalled_seconds === 'number'
          ? jobDetail.stalled_seconds
          : current.stalled_seconds,
    };

    const next = [...prev];
    next[idx] = updated;
    return next.sort(sortByRequestedOnDesc);
  });
};

const ProjectSockets = {
  connectSocket: function (
    projectId,
    userId,
    setLockedFields,
    setView,
    setActiveCollaborators,
    scriptRef,
    setScript,
    projectExports,
    setProjectExports
  ) {
    if (!socket || !socket.connected) {
      let baseUrl = process.env.REACT_APP_API_URL.replace('/api', '');
      socket = io.connect(baseUrl, { transports: ['websocket'] });

      socket.on('connect', () => {
        socket.emit('openProject', projectId, userId);

        // User connection events
        ProjectSockets.onEvent('userJoinedProject', (user, userList) => {
          setActiveCollaborators(userList);
        });

        ProjectSockets.onEvent('userLeftProject', (user, userList) => {
          setActiveCollaborators(userList);
        });

        // Locking and unlocking fields
        ProjectSockets.onEvent('unlockField', (record, recordId, field, userId) => {
          const fieldId = `${record}-${recordId}-${field}`;
          setLockedFields((prevFields) => {
            const updatedFields = new Map(prevFields);
            updatedFields.delete(fieldId);
            return updatedFields;
          });
        });

        ProjectSockets.onEvent('lockField', (record, recordId, field, userId) => {
          const fieldId = `${record}-${recordId}-${field}`;
          setLockedFields((prevFields) => {
            const updatedFields = new Map(prevFields);
            updatedFields.set(fieldId, { user: userId, time: new Date() });
            return updatedFields;
          });
        });

        ProjectSockets.onEvent('pageProcessed', (pageNumber) => {
          setScript((prevScript) => ({
            ...prevScript,
            last_processed_page: pageNumber,
          }));
        });

        ProjectSockets.onEvent('updateScript', (scriptId, field, value) => {
          if (field === 'freemium_slot' && value === 2) {
            toast.info('This is your last free project — scenes 1-5 are yours to keep editing.');
          }
          setScript((prevScript) => ({
            ...prevScript,
            [field]: value,
          }));
        });

        ProjectSockets.onEvent('addScene', (scene) => {
          addScene(setScript, scene);
        });

        ProjectSockets.onEvent('updateScene', (scene) => {
          setView((prevView) => ({
            ...prevView,
            updateDictionary: {
              ...prevView.updateDictionary,
              ['subjects']: true,
            },
          }));
          updateScene(setScript, scene);
        });

        ProjectSockets.onEvent('updateSceneManual', (sceneDetail) => {
          updateSceneManual(setScript, sceneDetail.sceneId, sceneDetail.field, sceneDetail.value);
        });

        ProjectSockets.onEvent('reorderScenes', (positions, editor) => {
          // The editor already applied this order locally when they dropped the scene.
          if (editor === userId) return;
          const orderedIds = [...positions]
            .sort((a, b) => a.scene_position - b.scene_position)
            .map((p) => p.id);
          // view.sceneIndex is an index into script.scenes, so it has to follow the
          // selected scene to its new slot or a different scene would open.
          const prevScenes = scriptRef.current?.scenes || [];
          setScript((prevScript) => ({
            ...prevScript,
            scenes: Utils.applySceneOrder(prevScript.scenes, orderedIds),
          }));
          setView((prevView) => {
            if (prevView.sceneIndex === null || prevView.sceneIndex === undefined) return prevView;
            const selectedId = prevScenes[prevView.sceneIndex]?.id;
            const newIndex = Utils.applySceneOrder(prevScenes, orderedIds).findIndex(
              (scene) => scene.id === selectedId
            );
            return newIndex === -1 ? prevView : { ...prevView, sceneIndex: newIndex };
          });
        });

        ProjectSockets.onEvent('addShot', (sceneId, newShot) => {
          let script = scriptRef.current;
          addShot(script, setScript, sceneId, newShot);
        });

        ProjectSockets.onEvent('updateShot', (shotId, field, newValue, editor) => {
          let script = scriptRef.current;
          if (
            editor === userId &&
            field !== 'image' &&
            field !== 'imageIsLoading' &&
            field !== 'imageGenerationFailed' &&
            field !== 'imageGenerationBlocked'
          ) {
            return;
          }
          updateShot(script, setScript, shotId, field, newValue);
        });

        ProjectSockets.onEvent('updateDictionary', (dictionary) => {
          setView((prevView) => ({
            ...prevView,
            updateDictionary: {
              ...prevView.updateDictionary,
              [dictionary]: true,
            },
          }));
        });
      });

      socket.on('exportUpdate', (jobId, jobDetail) => {
        updateExportProgress(jobId, jobDetail, setProjectExports);
      });

      // Error handling
      socket.on('connect_error', (error) => {
        console.error('WebSocket connection error:', error);
      });
      socket.on('disconnect', () => {
        console.log('WebSocket connection closed');
      });
      socket.on('error', (error) => {
        console.error('WebSocket error:', error);
      });
    }
  },

  emitLockField: function (type, typeId, field, setMyLockedField) {
    if (socket) {
      const fieldId = `${type}-${typeId}-${field}`;
      setMyLockedField(fieldId);
      socket.emit('lockProjectField', type, typeId, field);
    } else {
      console.log('Error: Lost server connection.');
    }
  },

  emitUnlockField: function (type, typeId, field, setMyLockedField) {
    if (socket) {
      setMyLockedField(null);
      socket.emit('unlockProjectField', type, typeId, field);
    } else {
      console.log('Error: Lost server connection.');
    }
  },

  onEvent: function (eventName, callback) {
    if (socket) {
      // Always clear any existing listener(s) for this event first. Without
      // this, re-registering on every 'connect' (which also fires on every
      // auto-reconnect after a network blip / laptop sleep / backgrounded
      // tab) stacks duplicate listeners on the same socket, so a single
      // real-time event ends up processed 2x, 3x, Nx for the rest of the
      // session.
      socket.off(eventName);
      if (callback) {
        socket.on(eventName, callback);
      }
    } else {
      console.log('Error: Lost server connection.');
    }
  },

  disconnectSocket: function () {
    if (socket) {
      socket.emit('userDisconnecting');
      socket.disconnect();
    }
  },
};

export default ProjectSockets;
